/**
 * labels.js — Anzeigenamen fuer Rohwerte (Welle 3 PR 4, §4.2).
 *
 * 🔴 Ein Rohwert darf nur dort stehen, wo er als WERT gebraucht wird (Payload,
 * `value=` einer Auswahl) — nie als sichtbare Beschriftung. Die Wertemengen
 * `brew_focus`, `skill_level`, `source`, `source_type` und `source_tier` wurden
 * vorher an mehreren Flaechen roh gerendert (`admin/TaxonomyGuide.jsx`,
 * `components/SourceManagerPage.jsx`, `discovery/CandidateRow.jsx`,
 * `discovery/CandidatePanel.jsx`), waehrend die Anzeigenamen als Kopien
 * danebenlagen (`ProductTaxonomyDetail.jsx`, `ProductsAttributesTable.jsx`,
 * `discovery/RunDialog.jsx`). Diese Datei ist die EINE Stelle, aus der alle
 * Flaechen lesen.
 *
 * Kanonisch ist die Schreibweise des Backends: `brew_focus` aus
 * `backend/canonical_taxonomy.py`, `source_type`/`source_tier` aus der
 * Discovery-Pipeline. Die Mengen sind geschlossen; ein unbekannter Wert wird
 * lesbar gemacht (Unterstrich → Leerzeichen, erster Buchstabe gross), damit
 * auch dann keine Rohform mit Unterstrich in der Flaeche steht.
 *
 * Anlass: `docs/superpowers/plans/2026-10-06-ux-feinschliff-welle-3.md` §4.2.
 */

/**
 * Anzeigenamen fuer `brew_focus` (Spiegel von `ProductTaxonomyDetail.jsx`
 * BREW_FOCUS_OPTIONS und `ProductsAttributesTable.jsx` BREW_FOCUS_OPTIONS, die
 * vorher zwei getrennte Kopien derselben fuenf Labels waren).
 */
export const BREW_FOCUS_LABELS = {
  espresso_only: 'Espresso Only',
  espresso_primary: 'Espresso Primary',
  allrounder: 'Allrounder',
  filter_primary: 'Filter Primary',
  filter_only: 'Filter Only',
};

/**
 * Anzeigenamen fuer `skill_level` — die EINE Quelle. Die beiden Flaechen
 * `ProductTaxonomyDetail.jsx` und `ProductsAttributesTable.jsx` fuehrten je
 * eine eigene SKILL_LEVEL_OPTIONS-Liste mit denselben vier Labels (dieselbe
 * Doppelung wie bei `brew_focus`); beide lesen jetzt hier.
 */
export const SKILL_LEVEL_LABELS = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  pro: 'Pro',
};

/**
 * Anzeigenamen fuer `source_type` (die Discovery-Quellentypen, siehe
 * `SourceManagerPage.jsx` SOURCE_TYPES). Vorher gab es gar keine Map — die
 * Liste rendete den Rohwert als Chip und als MenuItem-Text.
 */
export const SOURCE_TYPE_LABELS = {
  forum: 'Forum',
  review_site: 'Review site',
  manufacturer: 'Manufacturer',
  research_paper: 'Research paper',
  youtube_channel: 'YouTube channel',
  newsletter: 'Newsletter',
  podcast_transcript: 'Podcast transcript',
};

/**
 * Anzeigenamen fuer `source_tier` (die Discovery-Stufen, `_FETCHER_NAMES` in
 * `backend/discovery/tasks.py`). `RunDialog.jsx` und `ScanBanner.jsx` fuehrten
 * zwei Kopien; `ScanBanner` fehlte `bing_shopping` und fiel auf den Rohwert
 * zurueck. `google_shopping` fehlte ganz — `sourceTierLabel('google_shopping')`
 * fiel auf `lesbar()` → „Google shopping" (R16 W1).
 */
export const SOURCE_TIER_LABELS = {
  idealo: 'Idealo',
  geizhals: 'Geizhals',
  serpapi: 'SerpAPI',
  google_shopping: 'Google Shopping',
  bing_shopping: 'Bing Shopping',
};

/**
 * Anzeigenamen fuer `source` der Taxonomie — die Herkunft der Klassifikation
 * (`ProductTaxonomy.source`, `backend/models.py:1762`). Vorher gab es keine Map:
 * die Uebersichtskarte zog den Rohwert durch `lesbar(…)`, und aus
 * `canonical-v1` wurde „Canonical-v1" — ein Entwicklertext in der Flaeche
 * (Regel-16-Befund W1, Runde 1e).
 *
 * Die Menge ist gemessen, nicht gewaehlt:
 *   - `canonical-v1` — der EINZIGE Wert, den Prod traegt (258 Zeilen,
 *     gelesen 2026-10-07); geschrieben in `backend/taxonomy.py:1420`.
 *   - `rule-v1` — Vorgabe des Klassifizierers (`backend/taxonomy.py:169`) und
 *     der Relations-Batch (`backend/relations_batch.py:1840`).
 *   - `llm-v1` — im Modell genannt (`backend/models.py:1762`).
 * Ein unbekannter Wert faellt wie bei den uebrigen Maps auf `lesbar` zurueck,
 * damit nie ein Unterstrich in der Flaeche steht.
 */
export const TAXONOMY_SOURCE_LABELS = {
  'canonical-v1': 'Canonical type',
  'rule-v1': 'Rule-based',
  'llm-v1': 'AI-classified',
};

/**
 * Letzter Ausweg fuer einen Wert ohne Eintrag: lesbar, ohne Unterstrich.
 *
 * 🔴 Null-sicher mit Rueckfall und EXPORTIERT seit Welle 4 PR 1 (§5.5): der
 * Hilfetext des Override-Formulars ruft ihn direkt (`lesbar(v, '—')`). Bis
 * dahin war er modul-privat und haette aus einem leeren Wert „Undefined" gemacht.
 */
export function lesbar(value, fallback = '') {
  if (value == null || value === '') return fallback;
  const s = String(value);
  return s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/**
 * Anzeigename zu einem Rohwert aus einer geschlossenen Menge.
 * @param {object} map  die Label-Map (BREW_FOCUS_LABELS / SOURCE_TYPE_LABELS / SOURCE_TIER_LABELS)
 * @param {string} value  der Rohwert
 * @param {string} fallback  was bei leerem Wert erscheint (z. B. '—')
 */
function labelAus(map, value, fallback = '') {
  if (value == null || value === '') return fallback;
  return map[value] || lesbar(value);
}

/** `espresso_primary` → „Espresso Primary". */
export function brewFocusLabel(value, fallback = '') {
  return labelAus(BREW_FOCUS_LABELS, value, fallback);
}

/** `intermediate` → „Intermediate". */
export function skillLevelLabel(value, fallback = '') {
  return labelAus(SKILL_LEVEL_LABELS, value, fallback);
}

/** `youtube_channel` → „YouTube channel". */
export function sourceTypeLabel(value, fallback = '') {
  return labelAus(SOURCE_TYPE_LABELS, value, fallback);
}

/** `bing_shopping` → „Bing Shopping". */
export function sourceTierLabel(value, fallback = '') {
  return labelAus(SOURCE_TIER_LABELS, value, fallback);
}

/** `canonical-v1` → „Canonical type". */
export function taxonomySourceLabel(value, fallback = '') {
  return labelAus(TAXONOMY_SOURCE_LABELS, value, fallback);
}

// ---------------------------------------------------------------------------
// LSM-Ereignisnamen (Welle 4 PR 2, Karte 4)
// ---------------------------------------------------------------------------

/**
 * Anzeigenamen fuer die LSM-Ereignistypen, die der LSM-Mail-Parser aus einer
 * eingehenden Nachricht liest.
 *
 * Der Parser `_parse_lsm_return_email` (`backend/rma_routes.py:20079`) waehlt
 * `event_type` aus `_LSM_EVENT_MARKERS` (`:19915`, Schluessel `erfasst`/
 * `zugestellt`/`gebucht`) und faellt ohne Treffer auf `"unknown"` zurueck
 * (`:20093`). Die RMA-Inbox baut daraus zur Laufzeit `action_taken` und
 * `llm_intent` per f-String (`:21283` `f"lsm_{event_type}"`, `:21299`/`:21310`
 * `f"lsm_{event_type}_unmatched"`, `:23111` `f"lsm_auto_created_{event_type}"`,
 * `:20734` `f"lsm_return_{event_type}"`).
 *
 * 🔴 Diese Werte stehen NICHT in `ACTION_LABEL` und duerfen es nicht: die
 * f-Strings sind keine Literale, und `aktionsLabelWertemenge.test.js:224`
 * („kein Etikett fuer einen Wert, den niemand schreibt") macht einen statischen
 * Eintrag dort rot. Hier wird nachgeschlagen, was drueben abgeleitet wird — die
 * Ableitung selbst sitzt in `rma/RMAInbox.jsx` (`lsmEreignisLabel`).
 *
 * Wortlaut: die DREI Parser-Schluessel sind „Retoure bei LSM erfasst/
 * zugestellt/gebucht" (`rma_routes.py:20736-20740`, `event_labels`), englisch
 * wie die Oberflaeche seit Welle 3 PR 3. `unknown` hat dort KEINEN vierten
 * Schluessel — `:20741` faellt auf `f"LSM Retoure: {event_type}"` zurueck und
 * traegt deshalb keinen Wortlaut herueber (R16 HINWEIS 1). Eigener Text, weil
 * der Parser das Ereignis nicht EINORDNEN konnte; die Mail war lesbar, also
 * nicht „unreadable", sondern „not recognized".
 */
export const LSM_EVENT_LABELS = {
  erfasst:    'Recorded at LSM',
  zugestellt: 'Delivered at LSM',
  gebucht:    'Booked at LSM',
  unknown:    'LSM event (not recognized)',
};
